import { call, CallEffect, delay, put, PutEffect, takeLatest } from 'redux-saga/effects';

import { sendErrorToSentry } from 'js/logging';
import {
  convertedAudio,
  convertedImage,
  migrationError,
  MigrationStage,
  setAudioCount,
  setConversionProgress,
  setConversionComplete,
  setImagesCount,
  setMigrationStage,
  startMigration
} from 'js/reducers/legacyProjectMigration';
import ScribeModel from 'js/models/ScribeModel';
import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { AudioSource, ExistingScribeModel, VSCAssetImageSourceName } from 'js/types';
import { createProject } from 'js/shared/helpers/app-services/AppServices/projects';
import getScribeLengthMs from 'js/playback/helpers/getScribeLengthMs';
import { toSeconds } from 'js/editor/PlayerContext/timingHelpers';
import { saveAudioAssetMetadata } from 'js/actionCreators/audioActions';
import { MAX_IMAGE_FILE_UPLOAD_SIZE } from 'js/config/defaults';

import { processScribeFile, BlobDict, AudioBlobDict } from './processScribeFile';
import { convertAudioToMp3, AudioConversionResult } from './audioConversion';

type ConversionResult = {
  json: ScribeModel;
  imageBlobs: BlobDict;
  audioBlobs: AudioBlobDict;
};

export function* handleMigration(
  action: ReturnType<typeof startMigration>
): Generator<CallEffect | PutEffect, void, ConversionResult | number | ExistingScribeModel | AudioConversionResult> {
  try {
    yield put(setMigrationStage(MigrationStage.RebuildingTimelineAndCameras));
    yield put(setConversionProgress(1));
    const {
      json: project,
      imageBlobs,
      audioBlobs
    } = (yield call(processScribeFile, action.payload)) as ConversionResult;
    yield put(setConversionProgress(10));

    for (const [_blobPath, blob] of Object.entries(imageBlobs)) {
      if (blob.size > MAX_IMAGE_FILE_UPLOAD_SIZE) {
        throw new Error(
          `One of the images in your Scribe file exceeds the maximum allowed image size of ${MAX_IMAGE_FILE_UPLOAD_SIZE} bytes.`
        );
      }
    }

    const imagesLength = Object.keys(imageBlobs).length;
    const audiosLength = Object.keys(audioBlobs).length;
    yield put(setMigrationStage(MigrationStage.ConvertingImagesAndText));
    yield put(setImagesCount(imagesLength));
    yield put(setAudioCount(audiosLength));

    const imageAssetsManifest: { [blobPath: string]: number } = {};
    const imageProgressStep = 50 / imagesLength;
    let imageIndex = 0;
    for (const [blobPath, blob] of Object.entries(imageBlobs)) {
      const assetId = (yield call(appServices.uploadAsset, {
        file: blob,
        filename: blobPath,
        source: VSCAssetImageSourceName.USER_UPLOAD
      })) as number;
      imageAssetsManifest[blobPath] = assetId;
      yield put(convertedImage());
      imageIndex++;
      yield put(setConversionProgress(10 + imageIndex * imageProgressStep));
      yield delay(500);
    }

    project.elements.forEach(element => {
      if (element.type === 'Image' && element.image) {
        element.image.assetId = imageAssetsManifest[element.image.assetId];
      }
    });
    yield put(setConversionProgress(60));

    const audioAssetsManifest: { [blobPath: string]: number } = {};

    yield put(setMigrationStage(MigrationStage.ConvertingAudio));

    const audioProgressStep = 20 / audiosLength;
    let audioIndex = 0;
    for (const [blobPath, blob] of Object.entries(audioBlobs)) {
      const { durationSeconds, audioFile } = (yield call(convertAudioToMp3, blob.file)) as AudioConversionResult;

      const assetId = (yield call(appServices.uploadAsset, {
        file: audioFile,
        filename: blob.assetName,
        source: VSCAssetImageSourceName.USER_UPLOAD
      })) as number;

      yield put(
        saveAudioAssetMetadata({
          assetId,
          data: {
            assetId,
            dateUploaded: new Date(),
            wordBoundaryData: [],
            audioRole: blob.role,
            filename: blob.assetName,
            audioType: 'scene',
            audioDurationSeconds: durationSeconds
          },
          source: AudioSource.UPLOADED
        })
      );

      audioAssetsManifest[blobPath] = assetId;
      yield put(convertedAudio());
      audioIndex++;
      yield put(setConversionProgress(60 + audioIndex * audioProgressStep));
      yield delay(500);
    }

    project.audioClips?.forEach(audioClip => {
      audioClip.assetId = audioAssetsManifest[audioClip.assetId];
    });
    yield put(setConversionProgress(80));

    yield put(setMigrationStage(MigrationStage.FinalizingProject));
    const newProject = (yield call(createProject, project)) as ExistingScribeModel;
    const duration = toSeconds(getScribeLengthMs(newProject));

    yield put(setConversionProgress(100));
    yield put(setConversionComplete({ convertedProject: newProject, convertedProjectDurationSeconds: duration }));
  } catch (e) {
    console.error('Migration failed', e);
    sendErrorToSentry(e as Error, {
      tags: {
        operation: 'scribe-conversion',
        severity: 'error'
      }
    });
    yield put(migrationError((e as Error).message));
  }
}

export default function* legacyProjectMigrationSaga() {
  yield takeLatest(startMigration.type, handleMigration);
}
